import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
    FaBox, FaTruck, FaCheckCircle,
    FaTimesCircle, FaSearch, FaFilter,
    FaCalendarAlt, FaUser, FaPhoneAlt,
    FaEnvelope, FaMapMarkerAlt, FaCreditCard,
    FaChevronDown, FaChevronUp
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [expandedOrder, setExpandedOrder] = useState(null);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/api/orders`);
            if (res.status === 200) {
                // Sort orders by most recent
                const data = res.data;
                const sortedOrders = Array.isArray(data)
                    ? data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    : [];
                setOrders(sortedOrders);
            } else {
                toast.error("Failed to fetch orders");
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
            toast.error("Network error. Please check your connection.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const updateStatus = async (orderId, newStatus) => {
        try {
            // Updated to use axios for better error reporting and consistency
            const res = await axios.put(`${API_URL}/api/orders/${orderId}`, {
                status: newStatus,
            });

            if (res.status === 200) {
                setOrders(orders.map(o => o._id === orderId ? { ...o, status: newStatus } : o));
                toast.success(`Order status updated to ${newStatus}`);
            }
        } catch (err) {
            console.error("Error updating status:", err);
            // Show specific error message from backend if available
            const errorMessage = err.response?.data?.message || "Failed to update status";
            toast.error(errorMessage);
        }
    };

    const toggleOrderExpand = (id) => {
        setExpandedOrder(expandedOrder === id ? null : id);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Pending": return "bg-amber-100 text-amber-700 border-amber-200";
            case "Shipped": return "bg-blue-100 text-blue-700 border-blue-200";
            case "Delivered": return "bg-emerald-100 text-emerald-700 border-emerald-200";
            case "Cancelled": return "bg-rose-100 text-rose-700 border-rose-200";
            default: return "bg-gray-100 text-gray-700 border-gray-200";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "Pending": return <FaBox />;
            case "Shipped": return <FaTruck />;
            case "Delivered": return <FaCheckCircle />;
            case "Cancelled": return <FaTimesCircle />;
            default: return <FaBox />;
        }
    };

    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.userEmail.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === "All" || order.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const stats = {
        total: orders.length,
        pending: orders.filter(o => o.status === "Pending").length,
        delivered: orders.filter(o => o.status === "Delivered").length,
        revenue: orders.filter(o => o.status === "Delivered").reduce((acc, o) => acc + (o.total || 0), 0)
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen font-sans">
            <div className="max-w-7xl mx-auto">
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Order Management</h1>
                        <p className="text-gray-500 mt-1">Track and manage your customer orders instantly.</p>
                    </div>

                    <div className="flex bg-black text-white px-6 py-3 rounded-2xl shadow-xl hover:shadow-2xl transition-all">
                        <div className="flex flex-col border-r border-gray-700 pr-6 mr-6">
                            <span className="text-xs uppercase font-bold text-gray-400">Total Revenue</span>
                            <span className="text-xl font-bold">{stats.revenue.toLocaleString()} DT</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-xs uppercase font-bold text-gray-400">Total Orders</span>
                            <span className="text-xl font-bold">{stats.total}</span>
                        </div>
                    </div>
                </header>

                {/* Stats Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <StatCard title="All Orders" count={stats.total} icon={<FaBox />} color="bg-gray-100 text-gray-800" />
                    <StatCard title="Pending" count={stats.pending} icon={<FaBox className="animate-pulse" />} color="bg-amber-100 text-amber-700" />
                    <StatCard title="Delivered" count={stats.delivered} icon={<FaCheckCircle />} color="bg-emerald-100 text-emerald-700" />
                    <StatCard title="Active Revenue" count={`${stats.revenue} DT`} icon={<FaCreditCard />} color="bg-blue-100 text-blue-700" />
                </div>

                {/* Filters and Search */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by Order ID, Name or Email..."
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-black transition-all outline-none text-gray-700"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <div className="p-3 bg-gray-50 rounded-xl text-gray-500">
                            <FaFilter />
                        </div>
                        <select
                            className="bg-gray-50 border-none rounded-xl py-3 px-4 pr-10 focus:ring-2 focus:ring-black transition-all outline-none text-gray-700 font-medium cursor-pointer"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="All">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>

                {/* Orders List */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {filteredOrders.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-20 text-gray-400">
                            <FaBox size={64} className="mb-4 opacity-20" />
                            <p className="text-xl font-medium">No orders found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-100">
                                        <th className="p-4 font-bold text-gray-600 uppercase text-xs tracking-wider">Order Info</th>
                                        <th className="p-4 font-bold text-gray-600 uppercase text-xs tracking-wider">Customer</th>
                                        <th className="p-4 font-bold text-gray-600 uppercase text-xs tracking-wider">Amount</th>
                                        <th className="p-4 font-bold text-gray-600 uppercase text-xs tracking-wider">Status</th>
                                        <th className="p-4 font-bold text-gray-600 uppercase text-xs tracking-wider text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map((order) => (
                                        <React.Fragment key={order._id}>
                                            <tr
                                                className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer ${expandedOrder === order._id ? 'bg-gray-50/50' : ''}`}
                                                onClick={() => toggleOrderExpand(order._id)}
                                            >
                                                <td className="p-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gray-900 leading-tight">#{order._id.slice(-8).toUpperCase()}</span>
                                                        <span className="text-xs text-gray-500 flex items-center mt-1">
                                                            <FaCalendarAlt className="mr-1" /> {new Date(order.createdAt).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 font-bold">
                                                            {order.userName.charAt(0)}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-gray-900">{order.userName}</span>
                                                            <span className="text-xs text-gray-500">{order.userEmail}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <span className="font-bold text-gray-900">{order.total?.toLocaleString()} DT</span>
                                                </td>
                                                <td className="p-4">
                                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold ${getStatusColor(order.status)}`}>
                                                        {getStatusIcon(order.status)}
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-right">
                                                    <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                                                        <select
                                                            className="bg-white border text-xs font-bold py-1.5 px-2 rounded-lg outline-none cursor-pointer hover:border-black transition-all"
                                                            value={order.status}
                                                            onChange={(e) => updateStatus(order._id, e.target.value)}
                                                        >
                                                            <option value="Pending">Pending</option>
                                                            <option value="Shipped">Shipped</option>
                                                            <option value="Delivered">Delivered</option>
                                                            <option value="Cancelled">Cancelled</option>
                                                        </select>
                                                        <button className="p-2 hover:bg-white rounded-lg transition-all">
                                                            {expandedOrder === order._id ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expanded Details */}
                                            <AnimatePresence>
                                                {expandedOrder === order._id && (
                                                    <tr>
                                                        <td colSpan="5" className="p-0">
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: "auto", opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.3 }}
                                                                className="overflow-hidden bg-gray-50/30 p-6 border-b border-gray-100"
                                                            >
                                                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                                                    {/* Items */}
                                                                    <div className="lg:col-span-2">
                                                                        <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">Order Items ({order.items?.length})</h4>
                                                                        <div className="space-y-4">
                                                                            {order.items?.map((item, idx) => (
                                                                                <div key={idx} className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm">
                                                                                    <div className="flex items-center gap-4">
                                                                                        {item.selectedImage ? (
                                                                                            <img src={item.selectedImage} alt={item.productName} className="w-16 h-16 object-cover rounded-lg" />
                                                                                        ) : (
                                                                                            <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded-lg text-gray-400"><FaBox /></div>
                                                                                        )}
                                                                                        <div>
                                                                                            <h5 className="font-bold text-gray-900">{item.productName}</h5>
                                                                                            <p className="text-sm text-gray-500">
                                                                                                Size: <span className="text-black font-semibold mr-3">{item.selectedSize}</span>
                                                                                                Color: <span className="text-black font-semibold">{item.selectedColor || 'N/A'}</span>
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="text-right">
                                                                                        <p className="text-xs text-gray-400">{item.quantity} x {item.price} DT</p>
                                                                                        <p className="font-bold text-gray-900">{(item.quantity * item.price).toLocaleString()} DT</p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>

                                                                    {/* Delivery & Billing */}
                                                                    <div className="space-y-6">
                                                                        <div>
                                                                            <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">Customer Details</h4>
                                                                            <div className="bg-white p-5 rounded-xl shadow-sm space-y-3">
                                                                                <DetailItem icon={<FaUser />} text={order.userName} />
                                                                                <DetailItem icon={<FaEnvelope />} text={order.userEmail} />
                                                                                <DetailItem icon={<FaPhoneAlt />} text={order.userPhoneNumber} />
                                                                            </div>
                                                                        </div>

                                                                        <div>
                                                                            <h4 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">Shipping Address</h4>
                                                                            <div className="bg-white p-5 rounded-xl shadow-sm space-y-2">
                                                                                <div className="flex gap-2">
                                                                                    <FaMapMarkerAlt className="text-gray-400 shrink-0 mt-1" />
                                                                                    <div className="text-sm leading-relaxed text-gray-700">
                                                                                        <p className="font-semibold text-black">{order.address?.address}</p>
                                                                                        {order.address?.apartment && <p>{order.address.apartment}</p>}
                                                                                        <p>{order.address?.city}, {order.address?.state}</p>
                                                                                        {order.address?.postalCode && <p>Postal: {order.address.postalCode}</p>}
                                                                                        {order.address?.comment && (
                                                                                            <p className="mt-2 p-2 bg-amber-50 text-amber-700 rounded text-xs italic border border-amber-100">
                                                                                                "{order.address.comment}"
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </motion.div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </AnimatePresence>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, count, icon, color }) => (
    <div className={`p-5 rounded-2xl border border-transparent shadow-sm hover:shadow-md transition-all ${color} bg-white`}>
        <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold opacity-60 uppercase tracking-tight">{title}</span>
            <span className="text-xl">{icon}</span>
        </div>
        <div className="text-2xl font-black">{count}</div>
    </div>
);

const DetailItem = ({ icon, text }) => (
    <div className="flex items-center gap-3 text-sm text-gray-700">
        <span className="text-gray-400">{icon}</span>
        <span className="font-medium truncate">{text}</span>
    </div>
);

export default AdminOrders;
